import { Injectable, inject } from '@angular/core';
import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { HttpClientService } from '../api/http-client.service';
import { SKIP_ERROR_NOTIFY } from '../../core/interceptors/error.interceptor';
import {
  AuthSession,
  LoginRequestDto,
  LoginResponseDto,
  RefreshedTokens,
  RefreshRequestDto,
  RefreshResponseDto,
} from './auth.model';
import { dtoToRefreshedTokens, dtoToSession } from './auth.mapper';

const MSG_INVALID = 'Credenciales incorrectas. Verifique su usuario y contraseña.';

/**
 * Frontera de autenticación contra el backend GEOLIFTEC (`POST /auth/login`).
 *
 * Recibe `username`/`password`, recibe el `LoginResponseDto` ya desenvuelto del
 * envelope por `HttpClientService`, y lo traduce a `AuthSession` (dominio) vía
 * `dtoToSession`. El componente de login solo consume este Observable.
 */
@Injectable({ providedIn: 'root' })
export class AuthApiService {
  private readonly http = inject(HttpClientService);

  login(username: string, password: string): Observable<AuthSession> {
    const body: LoginRequestDto = { username, password };
    // El login maneja sus errores en línea, no por el toast global.
    const context = new HttpContext().set(SKIP_ERROR_NOTIFY, true);
    return this.http
      .post<LoginResponseDto>('auth/login', body, { context })
      .pipe(
        map(dtoToSession),
        catchError((err) => throwError(() => new Error(this.describeError(err)))),
      );
  }

  /**
   * Renueva el par de tokens. El backend rota ambos: el refresh anterior queda
   * inválido. Errores silenciados del toast global (los maneja el interceptor).
   */
  refresh(refreshToken: string): Observable<RefreshedTokens> {
    const body: RefreshRequestDto = { refresh_token: refreshToken };
    const context = new HttpContext().set(SKIP_ERROR_NOTIFY, true);
    return this.http
      .post<RefreshResponseDto>('auth/refresh', body, { context })
      .pipe(map(dtoToRefreshedTokens));
  }

  /** Revoca la sesión en el servidor (Bearer lo adjunta el interceptor). */
  logout(): Observable<void> {
    const context = new HttpContext().set(SKIP_ERROR_NOTIFY, true);
    return this.http.post<void>('auth/logout', {}, { context });
  }

  private describeError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const apiMessage = (err.error as { message?: string } | null)?.message;
      if (apiMessage) return apiMessage;
      if (err.status === 401 || err.status === 422) return MSG_INVALID;
      if (err.status === 0) return 'No se pudo conectar con el servidor. Revisa tu conexión.';
    }
    return 'No se pudo iniciar sesión. Intenta nuevamente.';
  }
}
